import Image from 'next/legacy/image'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { ResponsiveRender } from '@machobear/design-system'

import ENV_VARS from '@/config'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import Head from '@/components/head'

import avatar4 from './avatar4@2x.png'
import avatar5 from './avatar5@2x.png'
import avatar7 from './avatar7@2x.png'
import avatar9 from './avatar9@2x.png'
import avatar10 from './avatar10@2x.png'
import avatar11 from './avatar11@2x.png'
import heroBanner from './wbrothers-banner.jpg'

const { BASE_URL } = ENV_VARS

const BannerWrapper = styled.div`
  text-align: center;
  margin-bottom: 30px;
  height: 512px;
  overflow: hidden;
  position: relative;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    height: auto;
  }
`

const Video = styled.video`
  display: none;

  @media screen and (min-width: 700px) {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    min-height: 100%;
    min-width: 100%;
    z-index: -1;
  }
`

const Vignette = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(20, 20, 20, 0.35);
`

const Container = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  width: 100%;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    position: static;
    transform: translate(0, 0);
    padding-left: 25px;
    padding-right: 25px;
    padding-top: 60px;
    padding-bottom: 60px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
`

const CompanyName = styled.h1`
  color: ${colors.white};
  font-size: 48px;
  line-height: 65px;
  letter-spacing: 0.62px;
  margin: 0 0 45px;
  font-weight: 400;
  text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.4);

  @media screen and (max-width: 930px) {
    font-size: 40px;
    line-height: 57px;
    letter-spacing: 0.55px;
  }

  @media screen and (max-width: 670px) {
    font-size: 36px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 0.29px;
  }
`

const HorizontalRule = styled.div`
  width: 380px;
  margin-bottom: 40px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.6);

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 220px;
  }
`

const Description = styled.p`
  color: ${colors.white};
  font-size: 17px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin-bottom: 50px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  small {
    font-size: ${fontSizes.small};
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const DescriptionMobile = styled.p`
  color: ${colors.white};
  font-size: ${fontSizes.small};
  line-height: 22px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin-bottom: 50px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  @media only screen and (min-width: ${breakpoints.tabletPortrait}) {
    display: none;
  }
`

const SectionTitle = styled.h3`
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: bold;
  letter-spacing: -0.49px;
  line-height: 22px;
  text-align: center;
  margin-bottom: 30px;

  @media screen and (max-width: 700px) {
    display: none;
  }
`

const LandlordsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 95%;
  max-width: 1075px;
  margin: 10px auto 50px;

  @media screen and (max-width: 700px) {
    display: none;
  }
`

const AvatarsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: none;
  width: 100%;
  margin-top: 10px;
`

const AvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 132px;
  margin: 0 4px 0;

  @media screen and (max-width: 1060px) {
    max-width: 105px;
    margin: 0;
  }

  @media screen and (max-width: 820px) {
    max-width: 90px;
  }
`

const AvatarWrapper = styled.div`
  position: relative;
  width: 85px;
  height: 85px;
  margin-bottom: 10px;

  @media screen and (max-width: 900px) {
    width: 65px;
    height: 65px;
  }
`

const AvatarText = styled.p`
  color: ${colors.regular};
  font-size: 14px;
  font-weight: ${fontWeights.medium};
  line-height: 19px;
  letter-spacing: 0.23px;
  text-align: center;
  margin-bottom: 2px;

  @media screen and (max-width: 900px) {
    font-size: 11px;
    line-height: 14px;
    letter-spacing: 0.15px;
    margin-bottom: 0px;
  }
`

const AvatarRole = styled(AvatarText)`
  font-size: 14px;
  line-height: 16px;
  font-weight: 300;

  @media screen and (max-width: 900px) {
    font-size: 11px;
  }
`

const WBrothersProfile = ({ metadata }) => {
  const { t } = useTranslation('wbrothers')

  const profile = {
    banner: heroBanner.src,
    title: t('wbrothers:W Brothers Real Estate Group'),
    pageDescription: t('wbrothers:description.meta'),
    url: '/company/wbrothers',
    agents: [
      {
        name: 'Crystal Liu',
        role: t('wbrothers:Licensed Rental Advisor'),
        image: avatar5.src
      },
      {
        name: 'Van Wan',
        role: t('wbrothers:Licensed Rental Advisor'),
        image: avatar7.src
      },
      {
        name: 'Kevin Wang',
        role: t('wbrothers:Associate Broker'),
        image: avatar4.src
      },
      {
        name: 'Ruby Qi',
        role: t('wbrothers:Licensed Rental Advisor'),
        image: avatar10.src
      },
      {
        name: 'Rebecca Shan',
        role: t('wbrothers:Management Assistant'),
        image: avatar9.src
      },
      {
        name: 'Vanessa Zhou',
        role: t('wbrothers:Office Administrator'),
        image: avatar11.src
      }
    ]
  }

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        meta={{
          image: `${BASE_URL}/images/w-brothers-banner.jpg`,
          imageAlt: t('wbrothers:W Brothers Real Estate Group')
        }}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <BannerWrapper>
        <Image
          layout="fill"
          priority
          src={profile.banner}
          alt="W Brothers"
          sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
        />
        <Container>
          <ResponsiveRender above="tablet">
            <Video autoPlay loop muted>
              <source src="/videos/wbrothers-video.mp4" type="video/mp4" />
            </Video>
          </ResponsiveRender>
          <Vignette />
          <ContentContainer>
            <CompanyName>{profile.title}</CompanyName>
            <HorizontalRule />
            <Trans i18nKey="wbrothers:description.desktop">
              <Description>
                500 + Homes Professionally Rented since 2013 in Greater
                Vancouver Area
                <br />
                $400 + Million worth of Real Estate Under Current Property
                Management
                <br />
                <br />
                Professional Rental Consultation | Thorough Tenant Screening |
                Systematic Ongoing Management
                <br />
                <small>Sterling Management Services Ltd.</small>
              </Description>
            </Trans>
            <Trans i18nKey="wbrothers:description.mobile">
              <DescriptionMobile>
                500 + Luxury Homes Rented since 2013 in Metro Vancouver Area,
                $400 + Million worth of Real Estate under Current Property
                Management
                <br />
                <br />
                Professional Rental Consultation
                <br />
                Thorough Tenant Screening
                <br />
                Systematic Ongoing Management
                <br />
                <small>Sterling Management Services Ltd.</small>
              </DescriptionMobile>
            </Trans>
          </ContentContainer>
        </Container>
      </BannerWrapper>
      <LandlordsWrapper>
        <SectionTitle>{t('wbrothers:Meet Our Trusted Team')}</SectionTitle>
        <AvatarsWrapper>
          {profile.agents.map((agent, index) => (
            <AvatarContainer key={index}>
              <AvatarWrapper>
                <Image
                  src={agent.image}
                  alt=""
                  layout="fill"
                  objectFit="cover"
                />
              </AvatarWrapper>
              <AvatarText>{agent.name}</AvatarText>
              <AvatarRole>{agent.role}</AvatarRole>
            </AvatarContainer>
          ))}
        </AvatarsWrapper>
      </LandlordsWrapper>
      <SectionTitle>{t('wbrothers:Our Portfolio')}</SectionTitle>
    </>
  )
}

export default WBrothersProfile
